import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Check, Copy, Globe, ShieldCheck, Star } from "lucide-react";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { useReviewWidgetConfig, useSaveWidgetConfig } from "@/hooks/use-reputation";
import { formatDate } from "@/lib/i18n";

type WidgetStyle = "carousel" | "wall" | "badge" | "ticker" | "featured";

const STYLE_OPTIONS: Array<{
  value: WidgetStyle;
  label: string;
  description: string;
  recommendedLimit: number;
}> = [
  { value: "carousel", label: "Carousel", description: "Horizontal testimonials for heroes and landing pages.", recommendedLimit: 5 },
  { value: "wall", label: "Wall", description: "Multi-card grid for content-heavy pages.", recommendedLimit: 6 },
  { value: "badge", label: "Badge", description: "Compact aggregate rating block for headers and footers.", recommendedLimit: 3 },
  { value: "ticker", label: "Ticker", description: "Continuous quote strip for social proof sections.", recommendedLimit: 6 },
  { value: "featured", label: "Featured", description: "Single hero testimonial spotlight.", recommendedLimit: 1 },
];

function WidgetSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-64" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.05fr_0.95fr]">
        <SkeletonCard className="h-[760px]" />
        <SkeletonCard className="h-[760px]" />
      </div>
    </div>
  );
}

function renderStars(rating: number) {
  return Array.from({ length: 5 }, (_, index) => (
    <Star
      key={index}
      className={`h-4 w-4 ${index < rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`}
    />
  ));
}

function buildEmbedCode(scriptUrl: string, widgetStyle: WidgetStyle, limit: number) {
  const url = new URL(scriptUrl);
  url.searchParams.set("type", widgetStyle);
  url.searchParams.set("limit", String(limit));
  return `<script src="${url.toString()}" async></script>`;
}

export default function ReviewWidget() {
  const { data, isLoading, error } = useReviewWidgetConfig();
  const { mutate: saveConfig, isLoading: isSaving } = useSaveWidgetConfig();
  const [copiedStyle, setCopiedStyle] = useState<WidgetStyle | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<WidgetStyle>("featured");
  const [approvedIds, setApprovedIds] = useState<string[]>([]);
  const [allowedDomainsText, setAllowedDomainsText] = useState("");

  useEffect(() => {
    if (!data) {
      return;
    }

    setSelectedStyle(data.widgetStyle);
    setApprovedIds(data.approvedReviewIds);
    setAllowedDomainsText(data.allowedDomains.join("\n"));
  }, [data]);

  const eligibleReviews = useMemo(() => (
    data?.reviews.filter((review) => review.rating >= 4) ?? []
  ), [data?.reviews]);
  const approvedReviews = useMemo(() => (
    eligibleReviews.filter((review) => approvedIds.includes(review.id))
  ), [approvedIds, eligibleReviews]);
  const previewAverageRating = approvedReviews.length === 0
    ? 0
    : approvedReviews.reduce((total, review) => total + review.rating, 0) / approvedReviews.length;

  if (isLoading) return <WidgetSkeleton />;

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
        <AlertCircle className="h-4 w-4" />
        Failed to load the embeddable review widget configuration.
      </div>
    );
  }

  if (!data) {
    return (
      <EmptyState
        icon={<Star className="h-8 w-8" />}
        title="Review widget unavailable"
        description="The embeddable review widget is not configured for this tenant yet."
        variant="info"
      />
    );
  }

  const handleCopyEmbed = async (widgetStyle: WidgetStyle, limit: number) => {
    try {
      await navigator.clipboard.writeText(buildEmbedCode(data.scriptUrl, widgetStyle, limit));
      setCopiedStyle(widgetStyle);
      setTimeout(() => setCopiedStyle((current) => current === widgetStyle ? null : current), 2000);
    } catch {
      // Clipboard API requires secure context — ignore silently.
    }
  };

  const handleToggleApproval = (reviewId: string, isEligible: boolean) => {
    if (!isEligible) {
      return;
    }

    setApprovedIds((current) => (
      current.includes(reviewId)
        ? current.filter((id) => id !== reviewId)
        : [...current, reviewId]
    ));
  };

  const handleSave = () => {
    const allowedDomains = allowedDomainsText
      .split(/\n|,/)
      .map((value) => value.trim())
      .filter(Boolean);

    saveConfig({
      widgetStyle: selectedStyle,
      approvedReviewIds: approvedIds,
      allowedDomains,
    });
  };

  const selectedMeta = STYLE_OPTIONS.find((style) => style.value === selectedStyle) ?? STYLE_OPTIONS[0]!;
  const previewReviews = approvedReviews.slice(0, selectedMeta.recommendedLimit);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-foreground">Embeddable Review Widget</h1>
          <p className="text-sm text-muted-foreground">
            Approve only 4-star and 5-star reviews for public use, restrict allowed origins,
            and generate copy-ready embed snippets for five widget types.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--color-primary)]/90 disabled:opacity-50"
        >
          {isSaving ? "Saving..." : "Save Widget"}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.05fr_0.95fr]">
        <section className="rounded-2xl border border-border bg-background p-5 shadow-sm">
          <div className="space-y-5">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <ShieldCheck className="h-4 w-4 text-[var(--color-primary)]" />
                Public Display Approval
              </div>
              <p className="text-sm text-muted-foreground">
                The public widget only renders approved reviews rated 4 stars or higher. Lower-rated
                reviews remain internal by design.
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Widget Types
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                {STYLE_OPTIONS.map((style) => {
                  const embedCode = buildEmbedCode(data.scriptUrl, style.value, style.recommendedLimit);
                  const isSelected = selectedStyle === style.value;
                  return (
                    <div
                      key={style.value}
                      className={`stagger-item rounded-2xl border p-4 transition-colors ${
                        isSelected
                          ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5"
                          : "border-border bg-background"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedStyle(style.value)}
                        className="w-full text-left"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-foreground">{style.label}</p>
                            <p className="mt-1 text-xs text-muted-foreground">{style.description}</p>
                          </div>
                          <span className="rounded-full bg-muted px-2.5 py-1 text-[11px] text-muted-foreground">
                            {style.recommendedLimit} reviews
                          </span>
                        </div>
                      </button>
                      <div className="mt-3 rounded-xl bg-muted p-3">
                        <pre className="overflow-x-auto text-[11px] text-muted-foreground">{embedCode}</pre>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleCopyEmbed(style.value, style.recommendedLimit)}
                        className="mt-3 inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {copiedStyle === style.value ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                        {copiedStyle === style.value ? "Copied" : "Copy snippet"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <Globe className="h-4 w-4 text-[var(--color-primary)]" />
                Allowed Domains
              </div>
              <textarea
                value={allowedDomainsText}
                onChange={(event) => setAllowedDomainsText(event.target.value)}
                rows={4}
                placeholder={"https://www.example.com\nhttps://booking.partner-site.com"}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
              />
              <p className="text-xs text-muted-foreground">
                Enter one absolute origin per line. Public widget data requests are served only to these origins.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-foreground">Review Eligibility</p>
                <span className="text-xs text-muted-foreground">
                  {approvedIds.length} approved
                </span>
              </div>
              <div className="space-y-3">
                {data.reviews.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                    No reviews have been ingested yet.
                  </div>
                ) : (
                  data.reviews.map((review) => {
                    const isApproved = approvedIds.includes(review.id);
                    const isEligible = review.rating >= 4;

                    return (
                      <button
                        key={review.id}
                        type="button"
                        onClick={() => handleToggleApproval(review.id, isEligible)}
                        className={`stagger-item w-full rounded-2xl border p-4 text-left transition-colors ${
                          !isEligible
                            ? "cursor-not-allowed border-border bg-muted/40 opacity-70"
                            : isApproved
                              ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5"
                              : "border-border hover:border-[var(--color-primary)]/40"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-0.5">
                                {renderStars(review.rating)}
                              </div>
                              <span className="text-xs text-muted-foreground">{review.platform}</span>
                            </div>
                            <p className="text-sm text-foreground">
                              {review.text || "This review has no text body, but it can still contribute to the widget rating."}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {review.authorName} • {formatDate(review.publishedAt)}
                            </p>
                          </div>
                          <span
                            className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
                              !isEligible
                                ? "bg-muted text-muted-foreground"
                                : isApproved
                                  ? "bg-[var(--color-primary)] text-white"
                                  : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {!isEligible ? "Below 4 stars" : isApproved ? "Approved" : "Hidden"}
                          </span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-border bg-muted/30 p-5 shadow-sm">
          <div className="space-y-5">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Live Preview
              </p>
              <h2 className="mt-2 text-lg font-semibold text-foreground">Public widget runtime</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                This preview mirrors the five widget variants the public `page-serve` runtime now supports.
              </p>
            </div>

            <div className={`rounded-[24px] border border-border bg-background p-5 shadow-sm ${selectedStyle === "badge" ? "max-w-sm" : ""}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-base font-semibold text-foreground">Customer Reviews</p>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex items-center gap-0.5">
                      {renderStars(Math.round(previewAverageRating))}
                    </div>
                    <span className="text-sm font-medium text-foreground">{previewAverageRating.toFixed(1)}</span>
                    <span className="text-sm text-muted-foreground">
                      from {approvedReviews.length} approved reviews
                    </span>
                  </div>
                </div>
                <span className="rounded-full bg-[var(--color-primary)]/10 px-3 py-1 text-xs font-medium text-[var(--color-primary)]">
                  {selectedMeta.label}
                </span>
              </div>

              {selectedStyle === "badge" ? (
                <div className="mt-5 flex items-center justify-between gap-4 rounded-2xl border border-border bg-background/80 p-4">
                  <div>
                    <p className="text-3xl font-semibold text-[var(--color-primary)]">{previewAverageRating.toFixed(1)}</p>
                    <p className="mt-1 text-xs text-muted-foreground">Average rating</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-0.5 justify-end">{renderStars(Math.round(previewAverageRating))}</div>
                    <p className="mt-1 text-xs text-muted-foreground">{approvedReviews.length} approved reviews</p>
                  </div>
                </div>
              ) : selectedStyle === "ticker" ? (
                <div className="mt-5 overflow-hidden rounded-2xl border border-border bg-background/80 py-4">
                  <div className="flex gap-6 whitespace-nowrap px-4 text-sm text-foreground animate-[pulse_4s_ease-in-out_infinite]">
                    {(previewReviews.length ? previewReviews : approvedReviews.slice(0, 2)).map((review) => (
                      <span key={review.id}>
                        {review.text || "This approved review has no text body."} <strong>— {review.authorName}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              ) : selectedStyle === "wall" ? (
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  {previewReviews.map((review) => (
                    <article key={review.id} className="rounded-2xl border border-border bg-background/80 p-4">
                      <div className="flex items-center gap-0.5">{renderStars(review.rating)}</div>
                      <p className="mt-2 text-sm text-foreground">{review.text || "This approved review has no text body."}</p>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {review.authorName} • {formatDate(review.publishedAt)}
                      </p>
                    </article>
                  ))}
                </div>
              ) : selectedStyle === "featured" ? (
                previewReviews[0] ? (
                  <article className="mt-5 rounded-3xl border border-border bg-background/80 p-6">
                    <div className="flex items-center gap-0.5">{renderStars(previewReviews[0].rating)}</div>
                    <p className="mt-3 text-base leading-7 text-foreground">
                      {previewReviews[0].text || "This approved review has no text body."}
                    </p>
                    <p className="mt-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      {previewReviews[0].authorName} • {formatDate(previewReviews[0].publishedAt)}
                    </p>
                  </article>
                ) : null
              ) : (
                <div className="mt-5 grid grid-flow-col auto-cols-[minmax(240px,1fr)] gap-3 overflow-x-auto pb-1">
                  {previewReviews.map((review) => (
                    <article key={review.id} className="rounded-2xl border border-border bg-background/80 p-4">
                      <div className="flex items-center gap-0.5">{renderStars(review.rating)}</div>
                      <p className="mt-2 text-sm text-foreground">{review.text || "This approved review has no text body."}</p>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {review.authorName} • {formatDate(review.publishedAt)}
                      </p>
                    </article>
                  ))}
                </div>
              )}

              {approvedReviews.length === 0 && (
                <div className="mt-4 rounded-2xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                  Approve at least one review rated 4 stars or higher to preview the public widget.
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
